<template>
    <a-modal
        :visible="visible"
        width="95vw"
        :footer="null"
        :destroy-on-close="true"
        :closable="false"
        centered
        wrap-class-name="pmm-wrap"
        @cancel="$emit('close')"
    >
        <div class="pmm-window" v-if="provider">
            <div class="pmm-header">
                <div class="pmm-header-info">
                    <div class="pmm-title">
                        {{ provider.nomeFornecedor }}<template v-if="provider.cnpj"> · {{ provider.cnpj }}</template>
                    </div>

                    <div class="pmm-subtitle" v-if="provider.nomeVendedor">
                        {{ provider.nomeVendedor }}
                    </div>

                    <div class="pmm-meta">
                        <span>Cond. Pgto.: {{ provider.paymentCondition || "—" }}</span>
                        <span>Entrega prevista: {{ provider.expectedDate ? $options.filters.formatDateOnly(provider.expectedDate) : "—" }}</span>
                        <span>Tipo de entrega: {{ provider.deliveryType || "—" }}</span>
                        <span v-if="provider.minimumBilling">Fat. mín.: {{ provider.minimumBilling | currency }}</span>
                        <span class="pmm-total">Valor total: {{ total_provider(provider) | currency }}</span>
                    </div>
                </div>

                <div class="pmm-actions">
                    <template v-if="hasNavigation">
                        <a-tooltip title="Fornecedor anterior">
                            <a-button
                                class="pmm-icon-btn"
                                type="link"
                                icon="left"
                                :disabled="!canGoPrevious"
                                @click="$emit('previous-provider')"
                            />
                        </a-tooltip>

                        <span class="pmm-nav-counter">{{ currentIndex + 1 }}/{{ totalCount }}</span>

                        <a-tooltip title="Próximo fornecedor">
                            <a-button
                                class="pmm-icon-btn"
                                type="link"
                                icon="right"
                                :disabled="!canGoNext"
                                @click="$emit('next-provider')"
                            />
                        </a-tooltip>
                    </template>

                    <a-tooltip title="Fechar">
                        <a-button class="pmm-icon-btn" type="link" icon="close" size="small" @click="$emit('close')" />
                    </a-tooltip>
                </div>
            </div>

            <div class="pmm-content">
                <a-table
                    :columns="columns"
                    :data-source="provider.products"
                    row-key="id"
                    size="small"
                    :pagination="false"
                    :scroll="{ x: tableWidth }"
                    class="products-table"
                >
                    <template slot="selecionar" slot-scope="text, product">
                        <a-checkbox
                            :checked="isProductFullySelected(product)"
                            :indeterminate="isProductPartiallySelected(product)"
                            @change="toggleProductSelection(product, $event.target.checked)"
                        />
                    </template>

                    <template slot="produto" slot-scope="text, product">
                        <div class="pro-name">
                            {{ product.name }}

                            <span class="brand" v-if="product.brand">
                                {{ product.brand }}
                            </span>

                            <a-tooltip v-if="product.observations">
                                <template slot="title">
                                    {{ product.observations.toUpperCase() }}
                                </template>

                                <a-icon type="info-circle"/>
                            </a-tooltip>
                        </div>

                        <span class="repl-tag" v-if="replenishment(product)">
                            {{ replenishment(product) }}
                        </span>
                    </template>

                    <template slot="preco" slot-scope="text, product">
                        <div
                            class="pmm-price-main"
                            :class="{
                                lower: product.offer_price < product.last_purchase,
                                higher: product.offer_price > product.last_purchase
                            }"
                        >
                            {{ product.offer_price | currency }}
                        </div>

                        <div class="pmm-price-sub" v-if="product.last_purchase != null">
                            Últ.: {{ product.last_purchase | currency }}
                        </div>
                    </template>

                    <template slot="qtdEmb" slot-scope="text, product">
                        <div class="pmm-qtd-main">{{ quantity_sum(product.stores) }}</div>
                        <div class="pmm-qtd-sub">
                            {{
                                product.purchase_unity ||
                                `${product.packing.split(" ")[0]} ${product.packing.split(" ")[1]}`
                            }}
                        </div>
                    </template>

                    <template slot="total" slot-scope="text, product">
                        {{ total(product.stores) | currency }}
                    </template>

                    <template
                        v-for="store in stores"
                        :slot="'store_' + store.id + '_header'"
                    >
                        <a-tooltip :title="store.name" placement="top">
                            <span class="store-header-name">{{ store.name }}</span>
                        </a-tooltip>
                        <div class="store-header-total" :class="storeTotalClass(provider, store.id)">{{ storeTotal(provider, store.id) | currency }}</div>
                    </template>

                    <template
                        v-for="store in stores"
                        :slot="'store_' + store.id"
                        slot-scope="text, product"
                    >
                        <div class="matrix-store-cell" v-if="isCrossAtCd(product, store.id)">
                            <input
                                type="text"
                                class="matrix-qty-input ant-input"
                                :value="quantity_sum(product.stores)"
                                disabled
                            />
                        </div>
                        <div class="matrix-store-cell" v-else>
                            <span class="matrix-qty-wrap" :class="{ saved: !!savedCells[cellKey(filter_product(product, store.id))] }">
                                <input
                                    v-if="isActiveCell(product, store.id)"
                                    ref="activeInput"
                                    type="text"
                                    data-field="matrix-qty-modal"
                                    class="matrix-qty-input ant-input"
                                    v-model="activeValue"
                                    @input="handleMatrixQuantityInput($event, product, store.id)"
                                    @blur="commitActiveCell(product, store.id)"
                                    @keydown="handleMatrixKeydown($event, product, store)"
                                />
                                <input
                                    v-else-if="isOrderedCell(product, store.id)"
                                    type="text"
                                    class="matrix-qty-input ant-input"
                                    :value="orderedQuantity(product, store.id)"
                                    title="Pedido já gerado para esta loja com este fornecedor"
                                    disabled
                                />
                                <input
                                    v-else-if="isDepositAtNonCd(product, store.id)"
                                    type="text"
                                    class="matrix-qty-input ant-input"
                                    :value="displayQuantity(product, store.id)"
                                    disabled
                                />
                                <span
                                    v-else
                                    class="matrix-qty-input ant-input matrix-qty-display"
                                    @click="activateCell(product, store)"
                                >{{ displayQuantity(product, store.id) }}</span>
                            </span>
                            <div
                                class="matrix-suggested"
                                v-if="real_offer(product, store.id).request_quantity"
                            >
                                Requisitado: {{ real_offer(product, store.id).request_quantity }}
                            </div>
                            <div
                                class="matrix-suggested"
                                v-if="real_offer(product, store.id).inventory != null"
                            >
                                Estoque: {{ real_offer(product, store.id).inventory | stock }}
                            </div>
                        </div>
                    </template>
                </a-table>
            </div>
        </div>
    </a-modal>
</template>

<script>
import { replenishmentLabel } from "@/helpers/replenishment";
import ActionMenu from "@/components/popovers/action-menu.popover.vue";

const CROSS_STRATEGY = "CROSS";
const DEPOSIT_STRATEGY = "DEPOSIT";

const PRICE_LOCKED_TOOLTIP = "Pedido já gerado com este fornecedor — preço travado";

export default {
    name: "ProviderMatrixModal",
    components: { ActionMenu },
    props: {
        visible: {
            type: Boolean,
            default: false,
        },
        provider: {
            type: Object,
            default: null,
        },
        stores: {
            type: Array,
            default: () => [],
        },
        selectedOfferKeys: {
            type: Array,
            default: () => [],
        },
        orderedCells: {
            type: Object,
            default: () => ({}),
        },
        orderedOffers: {
            type: Object,
            default: () => ({}),
        },
        savedCells: {
            type: Object,
            default: () => ({}),
        },
        hasNavigation: {
            type: Boolean,
            default: false,
        },
        canGoPrevious: {
            type: Boolean,
            default: false,
        },
        canGoNext: {
            type: Boolean,
            default: false,
        },
        currentIndex: {
            type: Number,
            default: 0,
        },
        totalCount: {
            type: Number,
            default: 0,
        },
    },
    data() {
        return {
            activeCell: null,
            activeValue: '',
        };
    },
    computed: {
        cdStoreId() {
            const cd = this.stores.find((s) => s.is_cd);
            return cd ? cd.id : null;
        },
        tableWidth() {
            return 362 + (this.stores.length * 60);
        },
        columns() {
            const columns = [
                { title: "", key: "selecionar", width: 32, align: "center", scopedSlots: { customRender: "selecionar" } },
                { title: "Produto", key: "produto", width: 150, scopedSlots: { customRender: "produto" } },
                { title: "Preço", key: "preco", width: 60, align: "center", scopedSlots: { customRender: "preco" } },
                { title: "Qtd/Emb", key: "qtdEmb", width: 50, align: "center", scopedSlots: { customRender: "qtdEmb" } },
                { title: "Total", key: "total", width: 70, align: "center", scopedSlots: { customRender: "total" } },
            ];

            this.stores.forEach((store) => {
                columns.push({
                    key: `store_${store.id}`,
                    width: 60,
                    align: "center",
                    slots: { title: `store_${store.id}_header` },
                    scopedSlots: { customRender: `store_${store.id}` },
                });
            });

            return columns;
        },
    },
    methods: {
        isPriceLocked(productId, sellerId) {
            return !!this.orderedOffers[`${productId}-${sellerId}`];
        },
        isActiveCell(product, storeId) {
            return !!this.activeCell &&
                this.activeCell.productId === product.id &&
                this.activeCell.storeId === storeId;
        },
        displayQuantity(product, storeId) {
            return this.filter_product(product, storeId).requested_quantity || 0;
        },
        activateCell(product, store) {
            if (this.isCrossAtCd(product, store.id)) return;
            if (this.isDepositAtNonCd(product, store.id)) return;
            if (this.isOrderedCell(product, store.id)) return;

            const current = this.filter_product(product, store.id);

            this.activeValue = String(current.requested_quantity || '');
            this.activeCell = { productId: product.id, storeId: store.id };

            this.$emit("cell-focus", product, store.position, this.provider ? this.provider.products : null);

            this.$nextTick(() => {
                const refs = this.$refs.activeInput;
                const el = Array.isArray(refs) ? refs[0] : refs;

                if (el) {
                    el.focus();
                    el.select();
                }
            });
        },
        commitActiveCell(product, storeId) {
            this.saveQuantity(product, storeId);

            if (this.activeCell && this.activeCell.productId === product.id && this.activeCell.storeId === storeId) {
                this.activeCell = null;
            }
        },
        saveQuantity(product, storeId) {
            const store = product.stores.find((s) => s.store_id == storeId);

            if (!store) return;

            this.$emit("save-quantity", {
                key: this.cellKey(store),
                product_id: product.id,
                store_id: store.store_id,
                seller_id: product.seller_id,
            });
        },
        replenishment(product) {
            return replenishmentLabel(product.replenishment_strategy);
        },
        replaceStore(product, storeId, changes) {
            const index = product.stores.findIndex((s) => s.store_id == storeId);

            if (index === -1) return null;

            const updated = Object.freeze({ ...product.stores[index], ...changes });

            product.stores.splice(index, 1, updated);

            return updated;
        },
        handleMatrixQuantityInput(event, product, storeId) {
            let value = event.target.value.replace(/\D/g, "");

            const store = this.filter_product(product, storeId);

            if (!store.store_id) {
                return;
            }

            value = value.replace(/^0+(?=\d)/, "");

            if (+value > 999999) {
                value = "999999";
            }

            this.activeValue = value;
            event.target.value = value;

            const updated = this.replaceStore(product, storeId, { requested_quantity: value });

            if (updated) {
                this.$emit("quantity-edited", { key: this.cellKey(updated), requested_quantity: value });
            }
        },
        isCrossAtCd(product, storeId) {
            return product.replenishment_strategy === CROSS_STRATEGY && this.cdStoreId != null && storeId == this.cdStoreId;
        },
        orderedCellKey(product, storeId) {
            return `${product.id}-${storeId}-${product.provider_id}-${product.seller_id}`;
        },
        isOrderedCell(product, storeId) {
            return this.orderedCellKey(product, storeId) in this.orderedCells;
        },
        orderedQuantity(product, storeId) {
            return this.orderedCells[this.orderedCellKey(product, storeId)];
        },
        isDepositAtNonCd(product, storeId) {
            return product.replenishment_strategy === DEPOSIT_STRATEGY && this.cdStoreId != null && storeId != this.cdStoreId;
        },
        handleMatrixKeydown(event, product, store) {
            if (!["Enter", "ArrowDown", "ArrowUp", "ArrowLeft", "ArrowRight"].includes(event.key)) {
                return;
            }

            event.preventDefault();

            const rowIndex = this.provider.products.indexOf(product);
            const colIndex = this.stores.findIndex((s) => s.id == store.id);

            if (rowIndex === -1 || colIndex === -1) {
                return;
            }

            let nextRow = rowIndex;
            let nextCol = colIndex;

            switch (event.key) {
                case "Enter":
                case "ArrowDown":
                    nextRow = rowIndex + 1;
                    break;

                case "ArrowUp":
                    nextRow = rowIndex - 1;
                    break;

                case "ArrowRight":
                    nextCol = colIndex + 1;
                    break;

                case "ArrowLeft":
                    nextCol = colIndex - 1;
                    break;
            }

            nextRow = Math.max(0, Math.min(nextRow, this.provider.products.length - 1));
            nextCol = Math.max(0, Math.min(nextCol, this.stores.length - 1));

            const nextProduct = this.provider.products[nextRow];
            const nextStore = this.stores[nextCol];

            if (nextProduct && nextStore) {
                this.activateCell(nextProduct, nextStore);
            }
        },
        storeTotalClass(provider, storeId) {
            const total = this.storeTotal(provider, storeId);

            if (!total) return null;

            return total >= Number(provider.minimumBilling || 0) ? "reached" : "below";
        },
        storeTotal(provider, storeId) {
            return provider.products.reduce((sum, product) => {
                const quantity = this.isCrossAtCd(product, storeId)
                    ? this.quantity_sum(product.stores)
                    : (this.filter_product(product, storeId).requested_quantity || 0);

                const unitValue = product.is_generic == 0 && product.main_product_id == null
                    ? parseInt(product.packing.split(" ")[1]) * product.offer_price
                    : product.offer_price;

                return sum + quantity * unitValue;
            }, 0);
        },
        total_provider(provider) {
            let result = 0;
            provider.products.forEach((p) => {
                result += this.total(p.stores);
            });
            return result;
        },
        cellKey(store) {
            return `${store.id}-${store.store_id}-${store.provider_id}-${store.seller_id}`;
        },
        setSelection(keys) {
            this.$emit("update:selectedOfferKeys", keys);
        },
        selectableKeys(stores) {
            return stores.filter((store) => Number(store.requested_quantity) > 0).map((store) => this.cellKey(store));
        },
        isProductFullySelected(product) {
            const keys = this.selectableKeys(product.stores);
            return keys.length > 0 && keys.every((key) => this.selectedOfferKeys.includes(key));
        },
        isProductPartiallySelected(product) {
            const keys = this.selectableKeys(product.stores);
            const selectedCount = keys.filter((key) => this.selectedOfferKeys.includes(key)).length;
            return selectedCount > 0 && selectedCount < keys.length;
        },
        toggleProductSelection(product, checked) {
            if (checked) {
                const keys = this.selectableKeys(product.stores);
                this.setSelection(Array.from(new Set([...this.selectedOfferKeys, ...keys])));
                return;
            }

            const allKeys = product.stores.map((store) => this.cellKey(store));
            this.setSelection(this.selectedOfferKeys.filter((key) => !allKeys.includes(key)));
        },
        productActions(record) {
            const priceLocked = this.isPriceLocked(record.id, record.seller_id);

            return [
                { label: "Análise de giro", icon: "query_stats", iconClass: "action-info", onClick: () => this.$emit("giro", record, this.provider.products) },
                { label: "Negociar preço", icon: "handshake", iconClass: "action-success", disabled: priceLocked, tooltip: priceLocked ? PRICE_LOCKED_TOOLTIP : null, onClick: () => this.$emit("negotiate", record) },
                { label: "Alterar embalagem", icon: "inventory_2", iconClass: "action-warning", onClick: () => this.$emit("wrap", record) },
                { label: "Alterar vencedor", icon: "emoji_events", iconClass: "action-teal", onClick: () => this.$emit("change-winner", record) },
            ];
        },
        total(stores) {
            return stores.reduce((sum, store) => {
                return (
                    sum +
                    (store.requested_quantity || 0) *
                        (store.is_generic == 0 && store.main_product_id == null
                            ? parseInt(store.packing.split(" ")[1]) * store.offer_price
                            : store.offer_price)
                );
            }, 0);
        },
        quantity_sum(stores) {
            return stores.reduce((sum, store) => sum + Number(store.requested_quantity || 0), 0);
        },
        filter_product(product, store_id) {
            return (product.stores || []).find((s) => s.store_id == store_id) || {};
        },
        real_offer(product, store_id) {
            return (product.stores || []).find((s) => s.store_id == store_id && !s.isNewStore) || {};
        },
    },
};
</script>

<style lang="scss" scoped>
::v-deep(.products-table .ant-table-content) {
    overflow-x: auto;
}
::v-deep(.products-table table) {
    table-layout: fixed;
}
::v-deep(.products-table .ant-table-thead > tr > th),
::v-deep(.products-table .ant-table-tbody > tr > td) {
    padding: 6px !important;
}
::v-deep(.products-table .ant-table-tbody > tr > td) {
    vertical-align: top !important;
}
.repl-tag {
    display: inline-block;
    margin-top: 4px;
    padding: 1px 8px;
    border-radius: 10px;
    background: #f0f0f0;
    color: #595959;
    font-size: 11px;
    font-weight: 600;
    white-space: nowrap;
}
.pmm-price-main {
    font-weight: 600;
}
.pmm-price-main.lower {
    color: #389e0d;
}
.pmm-price-main.higher {
    color: #cf1322;
}
.pmm-price-sub,
.pmm-qtd-sub {
    font-size: 11px;
    color: #8c8c8c;
    white-space: nowrap;
}
.pmm-qtd-main {
    font-weight: 600;
}
.matrix-store-cell {
    display: flex;
    flex-direction: column;
    align-items: center;
}
.store-header-name {
    display: block;
    max-width: 100%;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}
.store-header-total {
    font-size: 11px;
    font-weight: 400;
    color: #8c8c8c;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}
.store-header-total.reached {
    color: #389e0d;
    font-weight: 600;
}
.store-header-total.below {
    color: #cf1322;
    font-weight: 600;
}
.matrix-qty-input {
    width: 48px;
    text-align: center;

    .ant-input {
        padding: 2px 4px;
    }
}
.matrix-qty-wrap {
    display: contents;
}
.matrix-qty-wrap.saved ::v-deep(.ant-input),
.matrix-qty-wrap.saved .matrix-qty-display {
    background-color: #13c7213d;
    color: green !important;
}
.matrix-qty-display {
    display: inline-block;
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
    cursor: text;
}
.matrix-suggested {
    font-size: 11px;
    color: #8c8c8c;
    text-align: center;
    margin-top: 2px;
    white-space: nowrap;
}
.pmm-window {
    display: flex;
    flex-direction: column;
    max-height: calc(100vh - 80px);
    width: 100%;
}
.pmm-header {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 16px;
    padding: 16px 20px;
    border-bottom: 1px solid #f0f0f0;
    flex-shrink: 0;
}
.pmm-title {
    font-weight: 600;
    font-size: 16px;
    color: #262626;
}
.pmm-subtitle {
    font-size: 12px;
    color: #8c8c8c;
    font-style: italic;
    margin-top: 2px;
}
.pmm-meta {
    display: flex;
    flex-wrap: wrap;
    gap: 4px 16px;
    margin-top: 8px;
    font-size: 12px;
    color: #595959;
}
.pmm-total {
    font-weight: 600;
    color: #262626;
}
.pmm-actions {
    display: flex;
    align-items: center;
    gap: 6px;
    flex-shrink: 0;
}
.pmm-icon-btn {
    flex-shrink: 0;
    color: #969595;
    padding: 0 2px;
}
.pmm-icon-btn:hover {
    color: #f4511e;
}
.pmm-nav-counter {
    flex-shrink: 0;
    font-size: 12px;
    font-weight: 600;
    color: #595959;
    white-space: nowrap;
}
.pmm-content {
    padding: 16px 20px;
    flex: 1;
    min-height: 0;
    overflow: auto;
}
</style>

<style lang="scss">
.pmm-wrap {
    .ant-modal-content {
        padding: 0;
        border-radius: 10px;
        overflow: hidden;
    }

    .ant-modal-body {
        padding: 0;
    }
}
</style>
